import type { PersonalizedFertilizerPdpResponse } from "@halm-club/halm-shared";
import { PortableText } from "../../../../shared/pdp/components/PortableText";
import "./FertilizerTimeline.css";

type TimelineSteps = NonNullable<
  PersonalizedFertilizerPdpResponse["soilAnalysisVariants"][number]["timelineSteps"]
>;
type TimelineSectionCopy =
  PersonalizedFertilizerPdpResponse["timelineSectionCopy"];

type FertilizerTimelineProps = {
  timelineSectionCopy: TimelineSectionCopy;
  timelineSteps?: TimelineSteps;
};

const hasTimelineSteps = (timelineSteps?: TimelineSteps) =>
  timelineSteps?.some((step) => step.substeps.length > 0) === true;

export function FertilizerTimeline({
  timelineSectionCopy,
  timelineSteps,
}: FertilizerTimelineProps) {
  if (!hasTimelineSteps(timelineSteps)) {
    return null;
  }

  return (
    <section
      className="personalized-fertilizer-pdp__timeline"
      data-test="fertilizer-timeline"
    >
      <div className="personalized-fertilizer-pdp__timeline-header">
        <PortableText
          className="personalized-fertilizer-pdp__timeline-heading"
          emphasisVariant="accent"
          value={timelineSectionCopy.heading}
        />
        <PortableText
          className="personalized-fertilizer-pdp__timeline-subheading"
          value={timelineSectionCopy.subheading}
        />
      </div>

      <div className="personalized-fertilizer-pdp__timeline-list">
        {timelineSteps?.map((step, stepIndex) => (
          <article
            className={[
              "personalized-fertilizer-pdp__timeline-step",
              step.isInThePast
                ? "personalized-fertilizer-pdp__timeline-step--past"
                : "",
              stepIndex % 2 === 0
                ? "personalized-fertilizer-pdp__timeline-step--top"
                : "personalized-fertilizer-pdp__timeline-step--bottom",
            ]
              .filter(Boolean)
              .join(" ")}
            data-test="fertilizer-timeline-step"
            data-time-tag={step.timeTag}
            key={`${step.timeTag}-${stepIndex}`}
          >
            <span
              aria-hidden="true"
              className="personalized-fertilizer-pdp__timeline-marker"
            />
            <div className="personalized-fertilizer-pdp__timeline-card">
              <span className="personalized-fertilizer-pdp__timeline-tag">
                {step.timeTag}
              </span>

              <div className="personalized-fertilizer-pdp__timeline-substeps">
                {step.substeps.map((substep, index) => {
                  const shouldRenderImage =
                    !step.isInThePast && !!substep.imageSource;

                  return (
                    <div
                      className={[
                        "personalized-fertilizer-pdp__timeline-substep",
                        shouldRenderImage
                          ? ""
                          : "personalized-fertilizer-pdp__timeline-substep--without-image",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      key={`${step.timeTag}-${substep.heading}-${index}`}
                    >
                      <div className="personalized-fertilizer-pdp__timeline-copy">
                        <h3 className="personalized-fertilizer-pdp__timeline-title">
                          {substep.heading}
                        </h3>
                        <PortableText
                          className="personalized-fertilizer-pdp__timeline-description"
                          value={substep.description}
                        />
                      </div>

                      {shouldRenderImage ? (
                        <div className="personalized-fertilizer-pdp__timeline-image-frame">
                          <img
                            alt=""
                            className="personalized-fertilizer-pdp__timeline-image"
                            loading="lazy"
                            src={substep.imageSource}
                          />
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
